import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { faPen } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { getPlaylistBackgroundImageUrl } from '@components/Playlists/basics/CoverImages';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import {
  FeedPostModalMainPlaylistHeaderFragmentDoc,
  type FragmentType,
  getFragment,
} from '@graphql/reactQuery';
import { useHover } from '@hooks/useHover';
import { usePlaylistPlayingState } from '@hooks/usePlaylistPlayingState';
import { BULLET } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedPostModalMainPlaylistHeader on Shelf {
    id
    releaseCount
    type
    name
    webappUri
    user {
      id
      username
      webappUri
    }
    ...PlaylistCoverImages
    ...PlaylistPreviewDropdownInfo
  }
`);

export const FeedPostModalMainPlaylistHeader = ({
  playlistFrag,
  onEditPlaylistClicked,
  allowEdit,
}: {
  playlistFrag: FragmentType<FeedPostModalMainPlaylistHeaderFragmentDoc>;
  onEditPlaylistClicked: () => void;
  allowEdit: boolean;
}) => {
  const playlist = getFragment(FeedPostModalMainPlaylistHeaderFragmentDoc, playlistFrag);
  const { id: playlistId, releaseCount, webappUri, user, name } = playlist;
  const { isPlaying, onPlayButtonClick, loading } = usePlaylistPlayingState({
    playlistId,
  });

  const [hovering, hoverProps] = useHover();
  return (
    <div className="relative h-[72px] p-4" {...hoverProps}>
      <div
        className="absolute left-0 top-0 h-full w-full rounded-t-md bg-cover bg-center"
        style={{ background: `url(${getPlaylistBackgroundImageUrl(playlist)})` }}
      >
        <div className="h-full w-full rounded-t-md bg-neutral900/50 bg-playlist-preview-bg backdrop-blur-[20px]" />
      </div>
      <div className="relative z-base flex h-full w-full items-center overflow-hidden text-white">
        <div className="mr-3 flex flex-shrink-0">
          <PlayButton
            color="white"
            size="s"
            onClick={onPlayButtonClick}
            playing={isPlaying}
            disabled={releaseCount === 0}
            elevated
            className="rounded-lg"
            loading={loading}
            currentListeningParty={null}
          />
        </div>
        <div className="flex w-full flex-col overflow-hidden text-white">
          <AccessibleLink className="flex items-center" pathnameUri={webappUriPath(webappUri)}>
            <div className=" overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-m font-medium text-white">
              {name}
            </div>
          </AccessibleLink>
          <div className="text-base flex text-base-s">
            {releaseCount > 0 ? (
              <>
                <div className="flex-shrink-0 whitespace-nowrap">
                  {releaseCount} {getSingularOrPlural({ count: releaseCount, word: 'sound' })}
                </div>
                {BULLET}
              </>
            ) : null}
            <AccessibleLink
              pathnameUri={webappUriPath(user.webappUri)}
              className="flex items-center overflow-hidden text-ellipsis whitespace-nowrap"
            >
              <p className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                {user.username}
              </p>
            </AccessibleLink>
          </div>
        </div>

        <IconButton
          className={twMerge(
            'flex-00auto',
            hovering ? 'md:block' : 'md:hidden',
            !allowEdit ? '!hidden' : '',
          )}
          variant="inverted"
          iconSize={18}
          size={32}
          icon={<FontAwesomeIcon icon={faPen} className=" text-white" />}
          onClick={onEditPlaylistClicked}
        />
      </div>
    </div>
  );
};
